// The one place that decides whether this page shows ads.
//
// Spark & Spiral Plus members (the account-level `premium` entitlement) play
// ad-free. index.html reads the cached answer below synchronously in <head>,
// before anything renders:
//   - member: adds `spark-plus` to <html> (CSS hides every .ad-slot from the
//     first paint) and never loads adsbygoogle.js;
//   - everyone else: loads adsbygoogle.js exactly as before.
// It also defines window.sparkAds and window.sparkLoadAds(), used here.
//
// The cache is a UX hint, not a security boundary: the entitlement itself is
// read from Supabase under RLS (src/account/client.js) and the cache is
// rewritten after every check. Keep the key and shape in sync with the inline
// script in index.html.
//
// This module stays tiny and has no Supabase import: signed-out visitors
// never download the account code.

export const PLUS_CACHE_KEY = 'spark_plus';
// supabase-js persists the session under sb-<project ref>-auth-token.
export const SESSION_STORAGE_KEY = 'sb-sgbjxmdlvipiirmiklai-auth-token';
// A cached "member" answer is trusted for at most this long without a fresh
// check (each page load with a session re-checks in the background).
const MAX_CACHE_MS = 7 * 24 * 60 * 60 * 1000;

const listeners = new Set();

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem(PLUS_CACHE_KEY) || 'null');
    if (c && c.v === 1 && c.uid && c.until > Date.now()) return c;
  } catch (e) { /* storage blocked: behave as signed out */ }
  return null;
}

export function adsEnabled() {
  if (typeof window === 'undefined') return true;
  if (window.sparkAds) return window.sparkAds.enabled !== false;
  return !readCache();
}

export function hasStoredSession() {
  try {
    return !!localStorage.getItem(SESSION_STORAGE_KEY);
  } catch (e) {
    return false;
  }
}

/** Subscribe to ad on/off changes. Returns an unsubscribe function. */
export function onAdsChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function apply(enabled) {
  const before = adsEnabled();
  window.sparkAds = { ...(window.sparkAds || {}), enabled };
  document.documentElement.classList.toggle('spark-plus', !enabled);
  if (before !== enabled) {
    listeners.forEach((fn) => fn(enabled));
    postAdsFlagToGames();
  }
}

/**
 * Record the result of a server entitlement check.
 *   uid: the signed-in user's id (null when signed out)
 *   member: true when the account has an active `premium` entitlement
 *   expiresAt: ms epoch of the entitlement's expiry, or null for lifetime
 */
export function setMembership({ uid, member, expiresAt }) {
  try {
    if (uid && member) {
      const cap = Date.now() + MAX_CACHE_MS;
      const until = expiresAt ? Math.min(expiresAt, cap) : cap;
      localStorage.setItem(PLUS_CACHE_KEY, JSON.stringify({ v: 1, uid, until }));
    } else {
      localStorage.removeItem(PLUS_CACHE_KEY);
    }
  } catch (e) { /* storage blocked: nothing to cache */ }
  apply(!(uid && member));
}

/** Load adsbygoogle.js if this visitor gets ads and it isn't loaded yet. */
export function loadAdsIfAllowed() {
  if (!adsEnabled()) return false;
  if (typeof window.sparkLoadAds === 'function') window.sparkLoadAds();
  return true;
}

// ── Embedded games ──────────────────────────────────────────────────────────
// Same-origin game iframes (Flutter web builds) can read the flag three ways:
//   1. window.parent.sparkAds.enabled
//   2. a {type: 'spark:ads', enabled} message, sent when each game iframe
//      loads, whenever membership changes, and in reply to the
//      {type: 'spark:ready'} handshake the spark_account web bridge sends;
//   3. localStorage 'spark_plus' (shared origin).
// Today's site builds show no ads (Flutter mobile ads don't run on the web,
// and the site builds use SPARK_PORTAL=none), so this is the hook for builds
// that add web ads later.

function adsMessage() {
  return { type: 'spark:ads', enabled: adsEnabled() };
}

export function postAdsFlagToGames() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('iframe').forEach((frame) => {
    try {
      frame.contentWindow?.postMessage(adsMessage(), window.location.origin);
    } catch (e) { /* cross-origin or detached frame */ }
  });
}

let bridgeInstalled = false;

export function installGameAdsBridge() {
  if (bridgeInstalled || typeof window === 'undefined') return;
  bridgeInstalled = true;
  // `load` doesn't bubble, but it can be captured at the document.
  document.addEventListener('load', (event) => {
    const frame = event.target;
    if (!frame || frame.tagName !== 'IFRAME') return;
    try {
      frame.contentWindow?.postMessage(adsMessage(), window.location.origin);
    } catch (e) { /* ignore */ }
  }, true);
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'spark:ready' || !event.source) return;
    event.source.postMessage(adsMessage(), window.location.origin);
  });
}
